import { Table } from '@tanstack/react-table';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { useRef } from 'react';
import { Button } from '../button/button';
import { scrollToTop } from './utils/scroll-to-top';

type TablePaginationProps<TData> = {
  table: Table<TData>;
};

export const TablePagination = <TData,>({ table }: TablePaginationProps<TData>) => {
  const rowCount = table.getRowCount();
  const pageSize = table.getState().pagination.pageSize;
  const paginationRef = useRef<HTMLDivElement>(null);

  if (!rowCount || rowCount <= pageSize) {
    return null;
  }

  return (
    <div ref={paginationRef} className="flex items-center justify-end gap-4">
      <div className="flex w-[100px] items-center justify-center text-xs font-medium">
        Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
      </div>

      <Button
        variant="outline"
        className="w-8 px-0"
        onClick={() => {
          table.setPageIndex(0);
          scrollToTop(paginationRef.current);
        }}
        disabled={!table.getCanPreviousPage()}
        aria-label="Go to first page"
      >
        <ChevronsLeft />
      </Button>

      <Button
        variant="outline"
        className="w-8 px-0"
        onClick={() => {
          table.previousPage();
          scrollToTop(paginationRef.current);
        }}
        disabled={!table.getCanPreviousPage()}
        aria-label="Go to previous page"
      >
        <ChevronLeft />
      </Button>

      <Button
        variant="outline"
        className="w-8 px-0"
        onClick={() => {
          table.nextPage();
          scrollToTop(paginationRef.current);
        }}
        disabled={!table.getCanNextPage()}
        aria-label="Go to next page"
      >
        <ChevronRight />
      </Button>

      <Button
        variant="outline"
        className="w-8 px-0"
        onClick={() => {
          table.setPageIndex(table.getPageCount() - 1);
          scrollToTop(paginationRef.current);
        }}
        disabled={!table.getCanNextPage()}
        aria-label="Go to last page"
      >
        <ChevronsRight />
      </Button>
    </div>
  );
};
